import { supabase } from "../utils/supabaseClient";
import { Currency, CreateCurrencyInput, UpdateCurrencyInput, UpsertBranchCurrencyResponse } from "../types/currency";

/**
 * Fetch currencies for a specific branch
 */
export const getCurrenciesByBranchId = async (
  branchId: string
): Promise<Currency[]> => {
  try {
    const { data, error } = await supabase.rpc(
      "get_currencies_by_branch_id",
      {
        p_branch_id: branchId,
      }
    );

    if (error) {
      throw error;
    }

    // Handle the response - it might be wrapped or an array
    if (Array.isArray(data)) {
      return data as unknown as Currency[];
    }

    // If data is an object with a data property
    if (data && typeof data === "object" && "data" in data) {
      const innerData = (data as any).data;
      return Array.isArray(innerData) ? innerData : [];
    }

    return [];
  } catch (err) {
    console.error("Error fetching branch currencies:", err);
    throw err;
  }
};

/**
 * Fetch a single currency by ID
 */
export const getCurrencyById = async (id: string): Promise<Currency | null> => {
  try {
    const { data, error } = await supabase.rpc("get_currency_by_id", {
      p_id: id,
    });

    if (error) {
      throw error;
    }

    return (data as unknown as Currency) || null;
  } catch (err) {
    console.error("Error fetching currency:", err);
    throw err;
  }
};

/**
 * Create a new currency using unified RPC
 * INSERT action: No id, no delete_id in payload
 */
export const createCurrency = async (
  currency: CreateCurrencyInput
): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc("upsert_branch_currency" as any, {
      payload: currency,
    });

    if (error) {
      throw error;
    }

    const response = data as unknown as UpsertBranchCurrencyResponse;
    if (!response.success) {
      throw new Error(response.message || "Failed to create currency");
    }

    return response.data;
  } catch (err) {
    console.error("Error creating currency:", err);
    throw err;
  }
};

/**
 * Update an existing currency using unified RPC
 * UPDATE action: id present, no delete_id
 */
export const updateCurrency = async (
  id: string,
  updates: UpdateCurrencyInput
): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc("upsert_branch_currency" as any, {
      payload: { id, ...updates },
    });

    if (error) {
      throw error;
    }

    const response = data as unknown as UpsertBranchCurrencyResponse;
    if (!response.success) {
      throw new Error(response.message || "Failed to update currency");
    }

    return response.data;
  } catch (err) {
    console.error("Error updating currency:", err);
    throw err;
  }
};

/**
 * Delete a currency by ID using unified RPC
 * DELETE action: delete_id takes priority over all other keys
 */
export const deleteCurrency = async (id: string): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc("upsert_branch_currency" as any, {
      payload: { delete_id: id },
    });

    if (error) {
      throw error;
    }

    const response = data as unknown as UpsertBranchCurrencyResponse;
    if (!response.success) {
      throw new Error(response.message || "Failed to delete currency");
    }

    return response.data;
  } catch (err) {
    console.error("Error deleting currency:", err);
    throw err;
  }
};

